import { useEffect, useState } from "react";
import { Satellite } from "lucide-react";
import PacketResults from "components/field/PacketResults";
import { rtctApi } from "services/rtctApi";
import {
  FAMILY_NAMES,
  esaText,
  familiesText,
  geometryText,
  localText,
  orbitsText,
  readOverpasses,
  readPassPlan,
  readPlanAnswer,
  untilText,
  zoneText,
} from "utils/overpasses";
import { useStableState } from "utils/stableState";

// Upcoming satellite passes (GET devices/<mac>/overpasses/): when Sentinel-1
// (radar, the team's InSAR), Sentinel-2 and Landsat image the node's spot over
// the next 14 days, worked out by the server from the node's GNSS fix (never
// shown) and CelesTrak's orbits. The ticked satellites are listed, and "Wake at
// these passes" (GET/POST overpasses/plan/) has the server keep a ground-truth
// packet (photo pair, GNSS fix, radar burst, upload) in the node's schedule at
// each of their passes in the next 7 days, or take them all out. Every change
// is previewed in plain words first; only "Send to the node" sends it. Passes
// move slowly, so the list is asked for every hour.
const POLL_MS = 60 * 60 * 1000;
const PLAN_DAYS = 7;

function usePassPlan(mac, refresh) {
  const [state, setState] = useStableState({ mac: "", plan: null, error: "" });
  useEffect(() => {
    if (!mac || typeof rtctApi.passPlan !== "function") return undefined;
    const abort = new AbortController();
    Promise.resolve(rtctApi.passPlan(mac, abort.signal))
      .then((data) => {
        if (!abort.signal.aborted) setState({ mac, plan: readPassPlan(data), error: "" });
      })
      .catch((e) => {
        if (!abort.signal.aborted) setState({ mac, plan: null, error: e?.message || "Pass plan unavailable" });
      });
    return () => abort.abort();
  }, [mac, refresh, setState]);
  return state.mac === mac ? state : { mac, plan: null, error: "" };
}

export function useOverpasses(mac, refresh = 0) {
  const [state, setState] = useStableState({ mac: "", data: null, error: "" });
  useEffect(() => {
    // A server (or a test mock) without the endpoint: nothing to draw.
    if (!mac || typeof rtctApi.overpasses !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(rtctApi.overpasses(mac, signal))
        .then((data) => {
          if (!signal.aborted) setState({ mac, data: readOverpasses(data), error: "" });
        })
        .catch((e) => {
          if (!signal.aborted)
            setState((old) => ({ ...old, mac, error: e?.message || "Satellite passes unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, refresh, setState]);
  return state.mac === mac ? state : { mac, data: null, error: "" };
}

export default function SatellitePasses({ mac }) {
  const [refresh, setRefresh] = useState(0);
  const { data, error } = useOverpasses(mac, refresh);
  const { plan, error: planError } = usePassPlan(mac, refresh);
  const [chosen, setChosen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => {
    setChosen(null);
    setPreview(null);
    setResult(null);
    setProblem("");
  }, [mac]);
  const zone = zoneText(data?.tzHours);
  const now = Date.now();
  if (!data && !error) return null;

  const all = plan?.all?.length ? plan.all : Object.keys(FAMILY_NAMES);
  const families = chosen || (plan?.families?.length ? plan.families : all.slice(0, 1));
  const toggle = (f) => {
    const next = families.includes(f) ? families.filter((x) => x !== f) : all.filter((x) => x === f || families.includes(x));
    if (next.length) setChosen(next);
  };
  const changed = !!plan?.enabled && families.join() !== plan.families.join();

  const ask = async (enabled, dryRun) => {
    setBusy(true);
    setProblem("");
    try {
      let answer;
      try {
        answer = await rtctApi.setPassPlan(mac, { enabled, families, days: PLAN_DAYS, dry_run: dryRun });
      } catch (e) {
        if (!e?.data) throw e;
        answer = e.data;
      }
      const read = readPlanAnswer(answer);
      if (!read) throw new Error("The server's answer could not be read");
      if (read.problem && (dryRun || !read.sent)) setProblem(read.problem);
      if (dryRun) setPreview(read.problem && !read.added.length && !read.dropped.length ? null : { ...read, enabled });
      else {
        setResult(read);
        setPreview(null);
        setChosen(null);
        setRefresh((n) => n + 1);
      }
    } catch (e) {
      setProblem(e?.message || "The schedule could not be built");
    } finally {
      setBusy(false);
    }
  };

  const passes = (data?.passes || []).filter((p) => !p.family || families.includes(p.family));
  return (
    <section className="field-panel field-passes" aria-label="Satellite passes">
      <div className="field-panel-head">
        <div>
          <h2>Satellite passes</h2>
          <p>Next {data?.days || 14} days{zone ? ` (${zone})` : ""}</p>
        </div>
        <span className="field-state">{plan?.enabled ? "Waking at passes" : `${passes.length} passes`}</span>
      </div>
      <div className="field-panel-body">
        {error && <div className="field-alert">{error}</div>}
        {data?.problem && <div className="field-notice">{data.problem.detail}.</div>}
        <fieldset className="field-passes-families">
          <legend>Satellites</legend>
          {all.map((f) => (
            <label key={f}>
              <input type="checkbox" checked={families.includes(f)} disabled={busy} onChange={() => toggle(f)} />{" "}
              {FAMILY_NAMES[f] || f}
            </label>
          ))}
        </fieldset>
        {passes.length > 0 && (
          <div className="field-table-scroll">
            <table className="field-table" aria-label="Satellite passes">
              <thead>
                <tr>
                  <th>Satellite</th>
                  <th>When</th>
                  <th>Geometry</th>
                  <th>ESA plan</th>
                  <th>Packet</th>
                </tr>
              </thead>
              <tbody>
                {passes.map((p) => (
                  <tr key={`${p.satellite}-${p.utc}`}>
                    <td>
                      <Satellite size={13} aria-hidden="true" /> {p.satellite}
                    </td>
                    <td>
                      {localText(p.local)}
                      <small className="field-passes-until"> {untilText(p.t, now)}</small>
                    </td>
                    <td>{geometryText(p)}</td>
                    <td>{esaText(p)}</td>
                    <td>{p.inSchedule ? (p.photo ? "Scheduled" : "Scheduled, no photo") : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && !data.problem && passes.length === 0 && (
          <div className="field-notice">
            No pass of {familiesText(families)} within view of this node in the next {data.days} days.
          </div>
        )}
        {families.includes("sentinel-1") && passes.some((p) => p.family === "sentinel-1") && (
          <p className="field-caption">
            Sentinel-1 is a radar: it looks to the side and images day and night. ESA does not image every pass, so each
            one is checked against ESA's published acquisition plan, and the node wakes only for the planned ones (or
            ones no plan reaches yet). At its dawn and dusk passes the sun is down, so the node takes no photo then:
            the GNSS fix, the radar burst and the upload only.
          </p>
        )}
        {data?.orbits && <p className="field-caption">{orbitsText(data.orbits)}</p>}
        {planError && <div className="field-alert">{planError}</div>}
        {plan && (
          <div className="field-passes-wake" aria-label="Wake at these passes">
            <p>
              <b>Wake at these passes: {plan.enabled ? "on" : "off"}.</b>{" "}
              {plan.enabled
                ? `The server keeps a ground-truth packet in the node's schedule at each ${familiesText(plan.families)} pass of the next ${plan.days || PLAN_DAYS} days.`
                : "The node wakes for no satellite pass."}
              {plan.enabled && plan.problem ? ` Last look: ${plan.problem}.` : ""}
            </p>
            {!preview && (
              <div className="field-actions">
                {(!plan.enabled || changed) && (
                  <button type="button" className="field-button" disabled={busy} onClick={() => ask(true, true)}>
                    {plan.enabled ? "Wake for the ticked satellites…" : "Turn on…"}
                  </button>
                )}
                {plan.enabled && (
                  <button type="button" className="field-button" disabled={busy} onClick={() => ask(false, true)}>
                    Turn off…
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {preview && (
          <div className="field-passes-plan" aria-label="Proposed schedule">
            {preview.unchanged ? (
              <p>Nothing to change: the node's schedule already matches.</p>
            ) : (
              <>
                {preview.added.length > 0 && (
                  <>
                    <p>Adds a ground-truth packet at:</p>
                    <ul>
                      {preview.added.map((p) => (
                        <li key={`${p.satellite}-${p.utc}`}>
                          {p.satellite}, {localText(p.local)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {preview.dropped.length > 0 && (
                  <>
                    <p>Takes out:</p>
                    <ul>
                      {preview.dropped.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            {preview.skipped.length > 0 && (
              <p className="field-caption">
                Left out: {preview.skipped.map((p) => `${p.satellite} ${localText(p.local)} (${p.why})`).join("; ")}.
              </p>
            )}
            <div className="field-actions">
              {!preview.unchanged && (
                <button type="button" className="field-button primary" disabled={busy} onClick={() => ask(preview.enabled, false)}>
                  Send to the node
                </button>
              )}
              {preview.unchanged && preview.enabled !== !!plan?.enabled && (
                <button type="button" className="field-button primary" disabled={busy} onClick={() => ask(preview.enabled, false)}>
                  {preview.enabled ? "Turn on" : "Turn off"}
                </button>
              )}
              <button type="button" className="field-button" disabled={busy} onClick={() => setPreview(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {problem && <div className="field-alert">{problem}</div>}
        {result && (
          <div className="field-notice" role="status">
            {result.sent
              ? "Sent. The node picks it up at its next upload."
              : result.outcome === "unchanged"
                ? `Saved: waking at passes is ${result.enabled ? "on" : "off"}; the schedule already matched.`
                : "Not sent: the server could not reach the broker. It tries again."}
          </div>
        )}
        <PacketResults mac={mac} />
      </div>
    </section>
  );
}
